import type { ClientMediaUnitDetail } from '@ooh/contracts';

type PriceLineItemKey =
  | 'clientProduction'
  | 'muralEcoProduction'
  | 'clientInstallation'
  | 'clientDismantling'
  | 'clientAdditionalFees';

type PriceBreakdownUnit = Pick<
  ClientMediaUnitDetail,
  'starterPrice' | 'discountedPrice' | PriceLineItemKey
>;

interface PriceLineItem {
  key: PriceLineItemKey;
  value: number | null;
  isOptional: boolean;
}

const PRICE_LINE_ITEMS = [
  { key: 'clientProduction', isOptional: false },
  { key: 'muralEcoProduction', isOptional: false },
  { key: 'clientInstallation', isOptional: false },
  { key: 'clientDismantling', isOptional: false },
  { key: 'clientAdditionalFees', isOptional: true },
] as const satisfies ReadonlyArray<{ key: PriceLineItemKey; isOptional: boolean }>;

export function computePriceBreakdown(unit: PriceBreakdownUnit) {
  const headlinePrice = unit.discountedPrice ?? unit.starterPrice;
  const lineItems: PriceLineItem[] = PRICE_LINE_ITEMS.map(({ key, isOptional }) => ({
    key,
    value: unit[key],
    isOptional,
  }));
  const coreLineItems = lineItems.filter((item) => !item.isOptional);
  const additionalLineItems = lineItems.filter((item) => item.isOptional);
  const sum =
    headlinePrice === null
      ? null
      : headlinePrice + coreLineItems.reduce((total, item) => total + (item.value ?? 0), 0);

  return {
    starterPrice: unit.starterPrice,
    discountedPrice: unit.discountedPrice,
    headlinePrice,
    showStarterAsReference: unit.starterPrice !== null && unit.discountedPrice !== null,
    coreLineItems,
    additionalLineItems,
    sum,
  };
}
